import { z } from "zod";
import i18n from "@/i18n";
const t = (key: string) => i18n.t(key, { ns: "validation" });
const tErp = (key: string) => i18n.t(key, { ns: "erp" });

/**
 * Item type enum
 */
export const itemTypeEnum = z.enum(["PRODUTO", "SERVICO", "PACOTE"]);

/**
 * Validation schema for catalog items
 * Only name and sale_price are required
 */
export const catalogItemSchema = z.object({
  // Required fields
  name: z.string().min(1, t("nameRequired")),
  sale_price: z.coerce.number().min(0, t("nonNegativeNumber")),

  // Type and classification
  type: itemTypeEnum.default("PRODUTO"),
  product_subtype_id: z.string().uuid().optional().nullable(),
  service_group_id: z.string().uuid().optional().nullable(),
  package_subtype_id: z.string().uuid().optional().nullable(),

  // Identification (empty string -> null so Postgres UNIQUE allows multiple null sku per customer)
  sku: z
    .string()
    .optional()
    .nullable()
    .transform((v) =>
      v === "" || (typeof v === "string" && !v.trim()) ? null : v
    ),
  barcode: z
    .string()
    .optional()
    .nullable()
    .transform((v) =>
      v === "" || (typeof v === "string" && !v.trim()) ? null : v
    ),

  // Classification
  category_id: z.string().uuid().optional().nullable(),
  brand_id: z.string().uuid().optional().nullable(),

  // Description
  description: z.string().optional().nullable(),

  // Pricing (optional - can calculate from cost + markup)
  cost_price: z.coerce.number().min(0).optional().nullable(),
  markup_percentage: z.coerce.number().min(0).max(1000).optional().nullable(),
  unit: z.string().default("UN"),

  // Stock tracking (for products only)
  is_perishable: z.boolean().default(false),
  track_by_batch: z.boolean().default(false),
  track_by_expiry: z.boolean().default(false),

  // Service-specific
  service_duration_minutes: z.coerce.number().int().positive().optional().nullable(),
  restrict_appointment_types: z.boolean().default(false),
  allowed_appointment_types: z.array(z.enum(["padrao", "retorno", "aplicacao", "banho_tosa"])).default([]),

  // Medication-specific (only if product_subtype is remédio/vacina/pomada)
  manufacturer: z.string().optional().nullable(),
  dose_ml: z.coerce.number().positive().optional().nullable(),
  storage_temp: z.string().optional().nullable(),
  registration_number: z.string().optional().nullable(),
  // Status
  is_active: z.boolean().default(true),

  // XML import tracking
  xml_import_id: z.string().uuid().optional().nullable(),

  // Fiscal fields (NFC-e/NFS-e)
  gtin: z.string().optional().nullable(),
  ncm: z.string().optional().nullable(),
  cest: z.string().optional().nullable(),
  cfop: z.string().optional().nullable(),
  cfop_outside_state: z.string().optional().nullable(),
  origin_code: z.string().optional().nullable(),
  icms_tax_situation: z.string().optional().nullable(),
  pis_tax_situation: z.string().optional().nullable(),
  cofins_tax_situation: z.string().optional().nullable(),
  ipi_tax_situation: z.string().optional().nullable(),
  ipi_enquadramento: z.string().optional().nullable(),
  ipi_base_calculo: z.coerce.number().min(0).optional().nullable(),
  ipi_aliquota: z.coerce.number().min(0).max(100).optional().nullable(),
  ipi_valor: z.coerce.number().min(0).optional().nullable(),
  nfse_service_list_item: z.string().optional().nullable(),
  nfse_service_code: z.string().optional().nullable(),
  nfse_cnae: z.string().optional().nullable(),
  nfse_iss_rate: z.coerce.number().min(0).max(100).optional().nullable(),
  nfse_service_description: z.string().max(2000).optional().nullable(),
  nfse_natureza_operacao: z.string().optional().nullable(),
  nfse_regime_especial_tributacao: z.string().optional().nullable(),
  nfse_optante_simples_nacional: z.boolean().optional().nullable(),
  nfse_iss_retido: z.boolean().optional().nullable(),
  nfse_base_calculo: z.coerce.number().min(0).optional().nullable(),
  nfse_valor_iss: z.coerce.number().min(0).optional().nullable(),
  nfse_valor_deducoes: z.coerce.number().min(0).optional().nullable(),
  nfse_desconto_incondicionado: z.coerce.number().min(0).optional().nullable(),
  nfse_desconto_condicionado: z.coerce.number().min(0).optional().nullable(),
  nfse_valor_pis: z.coerce.number().min(0).optional().nullable(),
  nfse_valor_cofins: z.coerce.number().min(0).optional().nullable(),
  nfse_ibs_cbs_situacao_tributaria: z.string().optional().nullable(),
  nfse_ibs_cbs_classificacao_tributaria: z.string().optional().nullable(),
  nfse_ibs_cbs_base_calculo: z.coerce.number().min(0).optional().nullable(),
  nfse_ibs_uf_aliquota: z.coerce.number().min(0).max(100).optional().nullable(),
  nfse_ibs_mun_aliquota: z.coerce.number().min(0).max(100).optional().nullable(),
  nfse_cbs_aliquota: z.coerce.number().min(0).max(100).optional().nullable(),
  nfse_ibs_uf_valor: z.coerce.number().min(0).optional().nullable(),
  nfse_ibs_mun_valor: z.coerce.number().min(0).optional().nullable(),
  nfse_cbs_valor: z.coerce.number().min(0).optional().nullable(),
  // NFC-e 2026: tributável e impostos
  unidade_tributavel: z.string().optional().nullable(),
  valor_total_tributos: z.coerce.number().min(0).optional().nullable(),
  aliquota_aproximada_tributos: z.coerce.number().min(0).max(100).optional().nullable(),
  fonte_valor_total_tributos: z.string().optional().nullable(),
  icms_base_calculo: z.coerce.number().min(0).optional().nullable(),
  icms_aliquota: z.coerce.number().min(0).max(100).optional().nullable(),
  icms_valor: z.coerce.number().min(0).optional().nullable(),
  icms_modalidade_base_calculo: z.string().optional().nullable(),
  pis_base_calculo: z.coerce.number().min(0).optional().nullable(),
  pis_aliquota: z.coerce.number().min(0).max(100).optional().nullable(),
  pis_valor: z.coerce.number().min(0).optional().nullable(),
  cofins_base_calculo: z.coerce.number().min(0).optional().nullable(),
  cofins_aliquota: z.coerce.number().min(0).max(100).optional().nullable(),
  cofins_valor: z.coerce.number().min(0).optional().nullable(),
  ibs_cbs_situacao_tributaria: z.string().optional().nullable(),
  ibs_cbs_classificacao_tributaria: z.string().optional().nullable(),
  ibs_cbs_base_calculo: z.coerce.number().min(0).optional().nullable(),
  cbs_aliquota: z.coerce.number().min(0).max(100).optional().nullable(),
  cbs_valor: z.coerce.number().min(0).optional().nullable(),
  ibs_uf_aliquota: z.coerce.number().min(0).max(100).optional().nullable(),
  ibs_uf_valor: z.coerce.number().min(0).optional().nullable(),
  ibs_mun_aliquota: z.coerce.number().min(0).max(100).optional().nullable(),
  ibs_mun_valor: z.coerce.number().min(0).optional().nullable(),
  ibs_valor_total: z.coerce.number().min(0).optional().nullable(),
  is_valor: z.coerce.number().min(0).optional().nullable(),

}).superRefine((data, ctx) => {
  if (data.type === "PACOTE" && data.sale_price <= 0) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["sale_price"],
      message: t("packagePricePositive"),
    });
  }

  if (data.restrict_appointment_types && data.allowed_appointment_types.length === 0) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["allowed_appointment_types"],
      message: tErp("itemForm.appointmentAvailability.selectAtLeastOne"),
    });
  }

  if (data.type === "PACOTE" && !data.package_subtype_id) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["package_subtype_id"],
      message: t("subtypeRequired"),
    });
  }
})
  .superRefine((data, ctx) => {
    if (data.type !== "PRODUTO") return;
    const ncm = (data.ncm ?? "").replace(/\D/g, "");
    const cfop = (data.cfop ?? "").replace(/\D/g, "");
    if (ncm.length > 0 && ncm.length !== 8) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: t("ncmEightDigits"), path: ["ncm"] });
    }
    if (cfop.length > 0 && cfop.length !== 4) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: t("cfopFourDigits"), path: ["cfop"] });
    }
  });

export type CatalogItemInput = z.infer<typeof catalogItemSchema>;

/**
 * Validation schema for XML import item selection
 */
export const xmlImportItemSelectionSchema = z.object({
  xmlItem: z.object({
    numeroItem: z.string().optional(),
    codigo: z.string(),
    descricao: z.string(),
    ncm: z.string(),
    cfop: z.string(),
    cest: z.string().optional(),
    origem: z.string().optional(),
    icmsSituacaoTributaria: z.string().optional(),
    pisSituacaoTributaria: z.string().optional(),
    cofinsSituacaoTributaria: z.string().optional(),
    unidade: z.string(),
    quantidade: z.number(),
    valorUnitario: z.number(),
    valorTotal: z.number(),
    ean: z.string().optional(),
    gtinTributavel: z.string().optional(),
    unidadeTributavel: z.string().optional(),
    quantidadeTributavel: z.number().optional(),
    valorUnitarioTributavel: z.number().optional(),
    valorDesconto: z.number().optional(),
    valorFrete: z.number().optional(),
    valorSeguro: z.number().optional(),
    valorOutrasDespesas: z.number().optional(),
    valorTotalTributos: z.number().optional(),
    icmsModalidadeBaseCalculo: z.string().optional(),
    icmsBaseCalculo: z.number().optional(),
    icmsAliquota: z.number().optional(),
    icmsValor: z.number().optional(),
    ipiSituacaoTributaria: z.string().optional(),
    ipiEnquadramento: z.string().optional(),
    ipiBaseCalculo: z.number().optional(),
    ipiAliquota: z.number().optional(),
    ipiValor: z.number().optional(),
    pisBaseCalculo: z.number().optional(),
    pisAliquota: z.number().optional(),
    pisValor: z.number().optional(),
    cofinsBaseCalculo: z.number().optional(),
    cofinsAliquota: z.number().optional(),
    cofinsValor: z.number().optional(),
    ibsCbsSituacaoTributaria: z.string().optional(),
    ibsCbsClassificacaoTributaria: z.string().optional(),
    ibsCbsBaseCalculo: z.number().optional(),
    cbsAliquota: z.number().optional(),
    cbsValor: z.number().optional(),
    ibsUfAliquota: z.number().optional(),
    ibsUfValor: z.number().optional(),
    ibsMunAliquota: z.number().optional(),
    ibsMunValor: z.number().optional(),
    ibsValorTotal: z.number().optional(),
    isValor: z.number().optional(),
  }),
  action: z.enum(["create", "update", "skip"]),
  existingItemId: z.string().uuid().optional(),
  mappedData: z.object({
    name: z.string().min(1, t("nameRequired")),
    sku: z.string().optional(),
    barcode: z.string().optional(),
    cost_price: z.number().min(0),
    sale_price: z.number().min(0),
    unit: z.string(),
    product_subtype_id: z.string().uuid().optional().nullable(),
    ncm: z.string().optional(),
    cfop: z.string().optional(),
    cest: z.string().optional(),
    gtin: z.string().optional(),
    unidade_tributavel: z.string().optional(),
    valor_total_tributos: z.number().optional(),
    aliquota_aproximada_tributos: z.number().optional(),
    fonte_valor_total_tributos: z.string().optional(),
    origem: z.string().optional(),
    icms_tax_situation: z.string().optional(),
    icms_base_calculo: z.number().optional(),
    icms_aliquota: z.number().optional(),
    icms_valor: z.number().optional(),
    icms_modalidade_base_calculo: z.string().optional(),
    ipi_tax_situation: z.string().optional(),
    ipi_enquadramento: z.string().optional(),
    ipi_base_calculo: z.number().optional(),
    ipi_aliquota: z.number().optional(),
    ipi_valor: z.number().optional(),
    pis_tax_situation: z.string().optional(),
    pis_base_calculo: z.number().optional(),
    pis_aliquota: z.number().optional(),
    pis_valor: z.number().optional(),
    cofins_tax_situation: z.string().optional(),
    cofins_base_calculo: z.number().optional(),
    cofins_aliquota: z.number().optional(),
    cofins_valor: z.number().optional(),
    ibs_cbs_situacao_tributaria: z.string().optional(),
    ibs_cbs_classificacao_tributaria: z.string().optional(),
    ibs_cbs_base_calculo: z.number().optional(),
    cbs_aliquota: z.number().optional(),
    cbs_valor: z.number().optional(),
    ibs_uf_aliquota: z.number().optional(),
    ibs_uf_valor: z.number().optional(),
    ibs_mun_aliquota: z.number().optional(),
    ibs_mun_valor: z.number().optional(),
    ibs_valor_total: z.number().optional(),
    is_valor: z.number().optional(),
  }),
});

export type XmlImportItemSelectionInput = z.infer<typeof xmlImportItemSelectionSchema>;
